import {
    CloseOutlined,
    FilterOutlined,
    GlobalOutlined,
    SettingOutlined,
    ShareAltOutlined,
    StarOutlined,
} from "@ant-design/icons";
import { Input, Popover, Typography } from "antd";
import { EmptyBaseNode } from "components/CC/Nodes/NodeBase/EmptyBaseNode.tsx";
import { NodeCategory } from "components/CC/types/enums.ts";
import {
    Children,
    cloneElement,
    ComponentProps,
    FC,
    isValidElement,
    PropsWithChildren,
    ReactElement,
    useState,
} from "react";

type BaseProps = Pick<ComponentProps<typeof Popover>, "children">;

const Base = ({ children }: BaseProps) => {
    const [open, setOpen] = useState(false);
    const close = () => setOpen(false);

    let validChild = Children.only(children);
    if (isMenu(validChild)) {
        validChild = cloneElement(validChild, { onClose: close });
    } else {
        validChild = null;
    }

    return (
        <Popover
            onOpenChange={(isOpen) => !isOpen && close()}
            placement={"right"}
            trigger={"click"}
            content={validChild}
            arrow={false}
            open={open}
            classNames={{
                body: '!p-0 rounded',
                root: "rounded"
            }}
        >
            <EmptyBaseNode onClick={() => setOpen(true)} />
        </Popover>
    );
};

// Tab configuration for the header
const MENU_TABS = [
    { key: "all", icon: StarOutlined, label: "All" },
    { key: NodeCategory.FlowControl, icon: FilterOutlined, label: "Flow Control" },
    { key: NodeCategory.DataOperations, icon: SettingOutlined, label: "Data Operations" },
    { key: NodeCategory.Connections, icon: GlobalOutlined, label: "Connections" },
    { key: NodeCategory.Composition, icon: ShareAltOutlined, label: "Composition" },
] as const;

type TabKey = "all" | NodeCategory;

interface MenuProps {
    onClose?: () => void;
    withSearch?: boolean;
    showTabs?: boolean;
}

const Menu = ({ children, onClose, withSearch = false, showTabs = true }: PropsWithChildren<MenuProps>) => {
    const [search, setSearch] = useState("");
    const [activeTab, setActiveTab] = useState<TabKey>("all");

    const validChildren = Children.toArray(children).filter(isMenuSection);

    return (
        <div className="flex flex-col" style={{ width: 280 }}>
            {/* Header with tabs */}
            <div className="flex items-center justify-between bg-primary px-2 py-2">
                {showTabs ? (
                    <div className="flex gap-1">
                        {MENU_TABS.map((tab) => {
                            const Icon = tab.icon;
                            const isActive = activeTab === tab.key;
                            return (
                                <div
                                    key={tab.key}
                                    onClick={() => setActiveTab(tab.key)}
                                    className={`w-8 h-8 flex items-center justify-center cursor-pointer rounded transition-colors ${
                                        isActive ? "bg-white/20" : "hover:bg-white/10"
                                    }`}
                                    title={tab.label}
                                >
                                    <Icon style={{ color: "white", fontSize: 16 }} />
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div />
                )}
                <div
                    onClick={onClose}
                    className="w-8 h-8 flex items-center justify-center cursor-pointer hover:bg-white/10 rounded"
                >
                    <CloseOutlined style={{ color: "white", fontSize: 14 }} />
                </div>
            </div>

            {/* Search */}
            {withSearch && (
                <div className="px-3 py-2 border-b border-gray-200">
                    <Input
                        placeholder="Type to find or filter elements"
                        value={search}
                        size={"small"}
                        onChange={({ target: { value } }) => setSearch(value)}
                        allowClear
                        suffix={null}
                    />
                </div>
            )}

            {/* Content */}
            <div className="overflow-y-auto px-3 py-2" style={{ maxHeight: 400 }}>
                {validChildren.map((child) =>
                    cloneElement(child, { search, activeTab, onClose, withClose: false })
                )}
            </div>
        </div>
    );
};

const isMenu = (element: unknown): element is ReactElement<MenuProps> => {
    return isValidElement(element) && element.type === Menu;
};

interface MenuSectionProps {
    title: string;
    category?: NodeCategory;
    onClose?: () => void;
    search?: string;
    withClose?: boolean;
    activeTab?: TabKey;
}

const MenuSection = ({ title, category, search, activeTab, children }: PropsWithChildren<MenuSectionProps>) => {
    const validChildren = Children.toArray(children).filter(isMenuSectionItem);

    // Filter by tab - if not "all", only show sections matching the tab
    if (activeTab && activeTab !== "all" && category !== activeTab) {
        return null;
    }

    // Filter children by search and check if any visible
    const filteredChildren = validChildren.map((child) => cloneElement(child, { search }));
    const hasVisibleChildren = filteredChildren.some((child) => {
        const props = child.props as MenuSectionItemProps;
        if (!search) return true;
        return props.title.toLowerCase().includes(search.toLowerCase());
    });

    if (!hasVisibleChildren) {
        return null;
    }

    return (
        <div className="flex flex-col gap-1 mb-3">
            <Typography.Text type="secondary" className="text-sm color-gray-700 font-semibold tracking-wide mb-1">
                {title}
            </Typography.Text>
            {filteredChildren}
        </div>
    );
};

const isMenuSection = (element: unknown): element is ReactElement<PropsWithChildren<MenuSectionProps>> => {
    return isValidElement(element) && element.type === MenuSection;
};

interface MenuSectionItemProps {
    title: string;
    onClick: () => void;
    color: string;
    icon?: FC<{ className?: string }>;
    search?: string;
}

const MenuSectionItem = ({ title, onClick, color, icon: Icon, search }: MenuSectionItemProps) => {
    if (search && !title.toLowerCase().includes(search.toLowerCase())) {
        return null;
    }

    return (
        <div
            className="flex gap-3 items-center cursor-pointer py-2 px-2 hover:bg-gray-100 rounded transition-colors"
            onClick={onClick}
        >
            <div
                style={{ backgroundColor: color }}
                className="w-8 h-8 rounded-[100%] flex items-center justify-center text-white"
            >
                {Icon && <Icon />}
            </div>
            <Typography.Text className={"font-semibold"}>{title}</Typography.Text>
        </div>
    );
};

const isMenuSectionItem = (element: unknown): element is ReactElement<MenuSectionItemProps> => {
    return isValidElement(element) && element.type === MenuSectionItem;
};

export const NodePopover = Object.assign(Base, {
    Menu,
    MenuSection,
    MenuSectionItem,
});
